import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { api, errorMessage } from '../lib/api.ts';
import { attachBuildProgress } from '../lib/buildProgress.ts';
import { rememberBuild, forgetBuild } from '../lib/activeBuild.ts';
import { confirmStagingSpend, stagingAckHeaders } from '../lib/stagingGuard.ts';
import NewDeckChrome from './new-deck/NewDeckChrome.tsx';
import { intakeChat, useIntakeMessages } from './new-deck/shared.tsx';
import type { ReactNode } from 'react';
import type { BuildEvent, BuildStartPayload, DeckSummaryPayload } from '../../shared/api.ts';

type WatchPhase = 'building' | 'done' | 'failed';

/** What the wizard passes in route state to save a round trip. Never required:
 *  arriving cold, the slug comes from the job's replayed `slug` event. */
type RouteHint = { slug?: string | null; title?: string | null; fresh?: boolean };

/**
 * `/build/:jobId` — watching one headless build: filmstrip, live preview, the
 * progress transcript, cancel and retry.
 *
 * This used to be the second half of `<NewDeckAgent>`, sharing one `phase` with
 * the intake form. That coupling is what made a build feel blocking: the
 * wizard re-attached to whatever build it remembered on every mount, so while
 * one was running you could never get back to the form to start another. The
 * build itself was always backgroundable (it is server-side, and the SSE
 * endpoint replays a job's whole history on re-attach) — only the UI trapped
 * you.
 *
 * So the form starts builds and redirects here; this route watches one. Two
 * builds = two of these, reachable from the header's builds indicator. Nothing
 * here is singular except the job it was handed.
 *
 * The wizard passes `{slug, title}` in route state to save a round trip, but
 * this route never depends on it: arriving cold (from the indicator, or a
 * pasted URL) the slug comes from the job's replayed `slug` event.
 */
export default function BuildWatch() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hint = (location.state || {}) as RouteHint;

  const { messages, busyText, setBusyText, say, userSaid } = useIntakeMessages();
  const [buildEngineLabel, setBuildEngineLabel] = useState<string | null>(null);
  const [phase, setPhase] = useState<WatchPhase>('building');
  const [slug, setSlug] = useState<string | null>(hint.slug || null);
  const [title] = useState(hint.title || null);
  const [summary, setSummary] = useState<DeckSummaryPayload | null>(null);
  const [convOpen, setConvOpen] = useState(true);
  // Which slide the stage shows. FOLLOW THE NEWEST UNTIL TOUCHED: watching a
  // build, the slide you want is almost always the one just written — but the
  // moment you pick one yourself, chasing you off it would be maddening. `null`
  // means "still following"; a number means the user chose.
  //
  // Before this the filmstrip took `onSelect={() => {}}` and `activeIdx={-1}`,
  // so its tiles were enabled buttons whose clicks went nowhere, and the preview
  // was pinned to slide 1 and snapped back to the cover every time a slide
  // landed (Alex, 2026-08-18).
  const [pickedIdx, setPickedIdx] = useState<number | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attachRef = useRef<{ detach: () => void } | null>(null);
  const mountedRef = useRef(true);
  const slugRef = useRef(slug);
  slugRef.current = slug;
  const lastAgentText = useRef(new Set<string>());
  /**
   * Say a line at most once for this job.
   *
   * Re-attaching REPLAYS the job's whole event history — by design, so a
   * reconnect catches up — and StrictMode alone attaches twice on mount, which
   * delivered every `error` and `done` line to the transcript twice. Only the
   * `text` events deduped before; everything the component says on its own
   * behalf did not. Keying on the full string means two genuinely different
   * problems still both appear.
   */
  function sayOnce(text: string) {
    const t = String(text || '').trim();
    if (!t || lastAgentText.current.has(t)) return;
    lastAgentText.current.add(t);
    say(t);
  }
  // Mirror of `phase` for the SSE handler, which closes over a stale render.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  // The job this route is watching. Nulled once terminal so a late reconnect
  // can't resurrect the stream.
  const liveRef = useRef<string | null>(jobId ?? null);

  // Both refs are set on MOUNT, not only cleared on unmount. StrictMode runs
  // effects mount → cleanup → mount, so a cleanup-only version leaves
  // `mountedRef` false (every summary poll discarded) and `liveRef` null — and
  // `liveRef` null makes `isCurrent()` false, so the re-attached progress
  // stream immediately cancels itself and the build appears to do nothing.
  useEffect(() => {
    mountedRef.current = true;
    liveRef.current = jobId ?? null;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
      attachRef.current?.detach();
      liveRef.current = null;
    };
  }, [jobId]);

  // Attach on mount. Re-remember the build as we do: landing here from the
  // indicator (or a pasted link) in a tab that never started it should still
  // survive that tab's refresh.
  useEffect(() => {
    if (!jobId) {
      navigate('/', { replace: true });
      return;
    }
    rememberBuild('new-deck', { jobId, slug: hint.slug, title: hint.title });
    setBusyText(hint.fresh ? 'Handing your brief to the deck builder…' : 'Reconnecting to your build…');
    sayOnce(
      hint.fresh
        ? 'The agent is on it: it reads your brief, plans the deck, then writes every slide. This takes a few minutes; slides appear in the filmstrip as they land. Safe to close this tab or start another deck, the build carries on.'
        : 'Picking this build up where it left off…',
    );
    if (hint.slug) pollSummary(hint.slug);
    attachProgress(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // SSE attach with bounded auto-reconnect — the job outlives any one
  // connection (re-attaching replays its history harmlessly; handleBuildEvent
  // is idempotent for everything but chat text, which dedupes via
  // lastAgentText). But a job that is GONE must END the wait: `phase` can only
  // leave 'building' via a `done` event, so retrying a 404 forever left this
  // screen spinning "Writing slides…" with no error and the retry chips
  // unreachable. See lib/buildProgress.js.
  function attachProgress(id: string) {
    attachRef.current?.detach();
    attachRef.current = attachBuildProgress({
      jobId: id,
      onEvent: handleBuildEvent,
      isCurrent: () => liveRef.current === id,
      onEnd: (reason) => {
        if (reason === 'done') return;
        if (!mountedRef.current || liveRef.current !== id) return;
        forgetBuild('new-deck', id);
        liveRef.current = null;
        sayOnce(
          reason === 'gone'
            ? 'I lost track of this build: it finished a while ago, or the server restarted. Anything it wrote is saved.'
            : "I lost the connection to this build and couldn't get it back. Anything it finished is saved; open the deck to see where it got to.",
        );
        setBusyText(null);
        setPhase('failed');
      },
    });
  }

  /** Returns true when the stream is finished (done event seen). */
  function handleBuildEvent(frame: unknown): boolean {
    // Asserted once: this is our own server's stream.
    const evt = frame as BuildEvent;
    switch (evt.type) {
      case 'slug':
        // Arriving cold: this is how the route learns which deck it is watching.
        if (evt.slug && slugRef.current !== evt.slug) {
          slugRef.current = evt.slug;
          setSlug(evt.slug);
          if (jobId) rememberBuild('new-deck', { jobId, slug: evt.slug, title: hint.title });
        }
        if (evt.slug && !pollRef.current) pollSummary(evt.slug);
        return false;
      case 'phase':
        setBusyText(evt.label + '…');
        return false;
      case 'slide':
        // Count comes from the polled summary; the event only drives the text.
        // First slide event = the deck exists on disk — start (or restart)
        // the summary poll here rather than at build start.
        if (!pollRef.current && slugRef.current) pollSummary(slugRef.current);
        setBusyText(`Writing slides: ${evt.count} so far…`);
        return false;
      case 'text': {
        const t = String(evt.text || '').trim();
        if (t.startsWith('Build engine:')) setBuildEngineLabel(t.slice('Build engine:'.length).trim());
        // Replays re-deliver old text events; drop ones we've shown. The
        // length filter sheds agent noise — but the model line is short AND
        // the one line a model A/B test needs, so it always passes.
        //
        // So do the SERVER's own progress verbs. They are short by nature
        // ("Reviewing: slides 1-12" is 24 characters) and they are the only
        // sign of life during the long post-build tail, where the agent has
        // stopped talking and the repair pass is still working. Without them
        // the transcript looks frozen and people reload. See
        // agent/__tests__/build-tail.test.ts, which pins both verbs.
        if (
          t &&
          (t.startsWith('Build model:') ||
            t.startsWith('Build engine:') ||
            t.startsWith('Claude model') ||
            t.startsWith('Build cost:') ||
            t.startsWith('Reviewing') ||
            t.startsWith('Writing section') ||
            // Stock-photo fetches, pushed in by the build-photo route. Short by
            // nature and the only sign of life while a photo is being searched,
            // downloaded and captioned.
            t.startsWith('Photo:') ||
            t.length > 40) &&
          !lastAgentText.current.has(t)
        ) {
          lastAgentText.current.add(t);
          say(t);
        }
        return false;
      }
      case 'error':
        sayOnce(`Build problem: ${evt.message}`);
        return false;
      case 'done': {
        liveRef.current = null;
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setBusyText(null);
        if (jobId) forgetBuild('new-deck', jobId); // terminal — nothing left to re-attach to
        if (evt.code === 0) {
          // Straight to the editor. This is NOT the old yank: reaching this
          // line means the user is still on this build's own watch route, so
          // opening the finished deck is what they were waiting for. Walk away
          // and this component is unmounted — nothing follows you.
          navigate(`/editor/${evt.slug}`, { replace: true });
        } else {
          setPhase('failed');
          // Populate the failed-build filmstrip with whatever landed, but the
          // component may already be gone by the time it resolves.
          loadSummary(evt.slug);
          sayOnce(
            'The build stopped before finishing. Anything already written is editable: open it in the editor, or retry.',
          );
        }
        return true;
      }
      default:
        return false;
    }
  }

  async function cancelBuild() {
    try {
      await api.post(`/api/wizard/build-cancel/${jobId}`, {});
    } catch (err) {
      // Silently swallowing this meant the "Cancel build" chip could do
      // literally nothing — no message, no phase change, spinner still going —
      // when the job had already gone (404). Say what happened; the caller
      // decides what to do next.
      say(`I couldn't stop that build: ${errorMessage(err)}. It may have already finished: check the dashboard.`);
    }
  }

  /** Retry server-side (session resume when possible — the intake bundle is
   *  still on disk). A retry is a NEW job, so this route hands over to the new
   *  one rather than trying to watch two. */
  async function retryBuild() {
    // A retry is a whole new build and costs one, so it asks again.
    if (!(await confirmStagingSpend('retrying this build'))) return;
    setPhase('building');
    setBusyText('Retrying the build…');
    try {
      const data = await api.post<BuildStartPayload>(
        `/api/wizard/build-retry/${jobId}`,
        {},
        {
          headers: await stagingAckHeaders(),
        },
      );
      if (jobId) forgetBuild('new-deck', jobId);
      navigate(`/build/${data.jobId}`, {
        replace: true,
        state: { slug: data.slug || slug, title, fresh: true },
      });
    } catch (err) {
      setBusyText(null);
      setPhase('failed');
      say(
        `I couldn't retry that build: ${errorMessage(err)}. Its record may have been reaped — start a new deck from the same brief instead.`,
      );
    }
  }

  async function loadSummary(s: string) {
    try {
      const data = await api.get<DeckSummaryPayload>(`/api/slides/${s}/_summary`);
      if (mountedRef.current) setSummary(data);
    } catch {}
  }
  function pollSummary(s: string) {
    loadSummary(s);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadSummary(s), 2500);
  }

  function handleSend(text: string) {
    userSaid(text);
    const trimmed = text.trim();
    if (phase === 'failed') {
      if (/retry|again/i.test(trimmed)) retryBuild();
      else if (slug && /open|editor/i.test(trimmed)) navigate(`/editor/${slug}`);
    } else if (slug && /open|editor/i.test(trimmed)) {
      navigate(`/editor/${slug}`);
    }
  }

  function chip(label: string, value?: string, title_?: string) {
    return (
      <button
        key={label}
        type="button"
        className="chip"
        title={title_ || undefined}
        onClick={() => handleSend(value ?? label)}
      >
        {label}
      </button>
    );
  }
  const chips: ReactNode[] | null =
    phase === 'building'
      ? [
          <button key="cancel" type="button" className="chip" onClick={cancelBuild}>
            Cancel build
          </button>,
          <button key="bg" type="button" className="chip" onClick={() => navigate('/')}>
            Leave it running
          </button>,
        ]
      : phase === 'failed'
        ? [chip('Retry', 'retry'), ...(slug ? [chip('Open what was built', 'open editor')] : [])]
        : null;

  const renderExtra = chips ? <div className="conversation__chips">{chips}</div> : null;

  const chat = intakeChat(messages, busyText, handleSend);

  const generating = phase === 'building';
  const slideCount = summary?.slides?.length || 0;
  // Clamp: a followed index has to stay inside a deck that is still growing, and
  // a picked one has to survive slides being written under it.
  const activeIdx = slideCount === 0 ? -1 : Math.min(pickedIdx ?? slideCount - 1, slideCount - 1);
  const blank = <p className="muted">The agent is planning your deck: slides appear here as they land.</p>;

  return (
    <NewDeckChrome
      deckName={title || slug || 'New deck'}
      generating={generating}
      buildMeta={[buildEngineLabel, slideCount > 0 ? `${slideCount} slides` : null].filter(Boolean).join(' · ')}
      buildEngineLabel={buildEngineLabel || 'Connecting to build…'}
      summary={summary}
      slug={slug}
      activeIdx={activeIdx}
      onSelect={setPickedIdx}
      blank={blank}
      chat={chat}
      commandBarPlaceholder={phase === 'failed' ? 'Retry, or open the editor…' : 'Building, watch the filmstrip…'}
      commandBarDisabled={false}
      renderExtra={renderExtra}
      convOpen={convOpen}
      onToggleConv={setConvOpen}
      onDrop={(e) => e.preventDefault()}
      fileInput={null}
    />
  );
}
